'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { walletApi, transferApi } from '../api/endpoints'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { showToast } from '../components/ui/Toast'
import CoinGlyph from '../components/lucky/CoinGlyph'
import { COIN_TYPES, type CoinMap, type CoinTransfer, type CoinType } from '../types'
import { coinNameKey } from '../utils/helpers'
import { useLocale, useT } from '../contexts/LocaleContext'
import { LOCALE_META } from '../i18n'

type TransferKind = 'admin' | 'user'
type PinMode = 'set' | 'unlock'

const emptyForm = {
  recipient: '',
  amount: '',
  walletAddress: '',
  coinType: 'bronze' as CoinType,
}

export default function CashOutPage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale, dir } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const queryClient = useQueryClient()

  const statusLabel = (status: string) => {
    if (status === 'completed') return t('cashOut.statusCompleted')
    if (status === 'pending') return t('cashOut.statusPending')
    if (status === 'approved') return t('cashOut.statusApproved')
    return t('cashOut.statusRejected')
  }
  const [sectionUnlocked, setSectionUnlocked] = useState(false)
  const [sessionPin, setSessionPin] = useState('')
  const [transferKind, setTransferKind] = useState<TransferKind>('admin')
  const [form, setForm] = useState(emptyForm)
  const [pinModal, setPinModal] = useState<PinMode | null>(null)
  const [pinInput, setPinInput] = useState('')

  const { data: walletData, isLoading } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getCoins(),
  })

  const { data: pinStatus } = useQuery({
    queryKey: ['transfers', 'has-pin'],
    queryFn: () => transferApi.hasPIN(),
  })

  const { data: transfersData } = useQuery({
    queryKey: ['transfers', 'list'],
    queryFn: () => transferApi.getTransfers(20, 0),
  })

  const setPINMutation = useMutation({
    mutationFn: (pin: string) => transferApi.setPIN(pin),
    onSuccess: (_data, pin) => {
      showToast(t('cashOut.pinSetOk'), 'success')
      setSessionPin(pin)
      setSectionUnlocked(true)
      setPinModal(null)
      setPinInput('')
      queryClient.invalidateQueries({ queryKey: ['transfers', 'has-pin'] })
    },
    onError: (err: any) => showToast(err.response?.data?.message || t('cashOut.pinSetErr'), 'error'),
  })

  const verifyPINMutation = useMutation({
    mutationFn: (pin: string) => transferApi.verifyPIN(pin),
    onSuccess: (_data, pin) => {
      showToast(t('cashOut.pinUnlockOk'), 'success')
      setSessionPin(pin)
      setSectionUnlocked(true)
      setPinModal(null)
      setPinInput('')
    },
    onError: (err: any) => showToast(err.response?.data?.message || t('cashOut.pinWrong'), 'error'),
  })

  const transferToUserMutation = useMutation({
    mutationFn: ({ recipient, coinType, amount, pin }: { recipient: string; coinType: CoinType; amount: number; pin: string }) =>
      transferApi.transferToUser(recipient, coinType, amount, pin),
    onSuccess: () => {
      showToast(t('cashOut.transferOk'), 'success')
      setForm(emptyForm)
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transfers', 'list'] })
    },
    onError: (err: any) => showToast(err.response?.data?.message || t('cashOut.transferErr'), 'error'),
  })

  const requestAdminTransferMutation = useMutation({
    mutationFn: ({ coinType, amount, walletAddress, pin }: { coinType: CoinType; amount: number; walletAddress: string; pin: string }) =>
      transferApi.requestAdminTransfer(coinType, amount, walletAddress, pin),
    onSuccess: () => {
      showToast(t('cashOut.adminOk'), 'success')
      setForm(emptyForm)
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transfers', 'list'] })
    },
    onError: (err: any) => showToast(err.response?.data?.message || t('cashOut.adminErr'), 'error'),
  })

  const openSection = () => {
    if (!pinStatus?.has_pin) {
      setPinModal('set')
      return
    }
    setPinModal('unlock')
  }

  const handlePINSubmit = () => {
    if (pinInput.length < 4 || pinInput.length > 6) {
      showToast(t('cashOut.errPinLength'), 'error')
      return
    }
    if (pinModal === 'set') {
      setPINMutation.mutate(pinInput)
    } else {
      verifyPINMutation.mutate(pinInput)
    }
  }

  const handleSubmit = () => {
    if (!sessionPin) {
      showToast(t('cashOut.errUnlockFirst'), 'error')
      setSectionUnlocked(false)
      return
    }
    const amount = parseInt(form.amount, 10)
    if (!amount || amount < 1) {
      showToast(t('cashOut.errAmount'), 'error')
      return
    }
    const balance = coins[form.coinType] || 0
    if (amount > balance) {
      showToast(t('cashOut.errInsufficient'), 'error')
      return
    }

    if (transferKind === 'user') {
      const recipient = form.recipient.trim()
      if (!recipient) {
        showToast(t('cashOut.errRecipient'), 'error')
        return
      }
      transferToUserMutation.mutate({
        recipient,
        coinType: form.coinType,
        amount,
        pin: sessionPin,
      })
      return
    }

    if (!form.walletAddress.trim()) {
      showToast(t('cashOut.errWallet'), 'error')
      return
    }
    requestAdminTransferMutation.mutate({
      coinType: form.coinType,
      amount,
      walletAddress: form.walletAddress.trim(),
      pin: sessionPin,
    })
  }

  const coins = walletData?.coins || { bronze: 0, silver: 0, gold: 0, diamond: 0 }
  const transfers = transfersData?.transfers || []
  const busy = transferToUserMutation.isPending || requestAdminTransferMutation.isPending

  if (isLoading) {
    return (
      <div className="lucky-wallet mini-app-container pb-20 min-h-screen flex items-center justify-center">
        <div className="lucky-wallet__empty">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="lucky-wallet mini-app-container pb-20 min-h-screen">
      <div className="lucky-wallet__inner">
        <header className="lucky-wallet__header">
          <button
            type="button"
            className="lucky-wallet__back"
            onClick={() => navigate('/wallet')}
            aria-label={t('common.back')}
          >
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-wallet__header-icon">
            <CoinGlyph type="diamond" size={28} />
          </div>
          <div className="lucky-wallet__header-grow">
            <h1>{t('cashOut.title')}</h1>
            <p>{t('cashOut.subtitle')}</p>
          </div>
          <button type="button" className="lucky-wallet__link" onClick={() => navigate('/wallet')}>
            {t('wallet.title')}
          </button>
        </header>

        <section className="lucky-wallet__card">
          <p className="lucky-wallet__hint">
            {t('cashOut.intro')}
          </p>
          <div className="lucky-wallet__coins lucky-wallet__coins--compact">
            {COIN_TYPES.map((coinType) => (
              <div key={coinType} className="lucky-wallet__coin">
                <CoinGlyph type={coinType as CoinType} size={22} />
                <strong>{coins[coinType as keyof CoinMap]}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="lucky-wallet__card">
          <div className="lucky-wallet__lock-row">
            <div>
              <h2 className="lucky-wallet__title">{t('cashOut.sectionTitle')}</h2>
              <p className="lucky-wallet__hint" style={{ marginBottom: 0 }}>
                {!pinStatus?.has_pin
                  ? t('cashOut.pinSetupHint')
                  : sectionUnlocked
                    ? t('cashOut.unlockedHint')
                    : t('cashOut.pinEnterHint')}
              </p>
            </div>
            {!sectionUnlocked ? (
              <button type="button" className="lucky-wallet__btn-sec" onClick={openSection}>
                {!pinStatus?.has_pin ? t('cashOut.setPin') : t('cashOut.unlockPinBtn')}
              </button>
            ) : (
              <button
                type="button"
                className="lucky-wallet__btn-sec"
                onClick={() => {
                  setSectionUnlocked(false)
                  setSessionPin('')
                  setForm(emptyForm)
                }}
              >
                {t('cashOut.lock')}
              </button>
            )}
          </div>

          {!sectionUnlocked ? (
            <div className="lucky-wallet__locked">
              {t('cashOut.lockedBanner')}
            </div>
          ) : (
            <div className="lucky-wallet__form">
              <div className="lucky-wallet__tabs">
                <button
                  type="button"
                  className={`lucky-wallet__tab ${transferKind === 'admin' ? 'is-on' : ''}`}
                  onClick={() => {
                    setTransferKind('admin')
                    setForm(emptyForm)
                  }}
                >
                  {t('cashOut.tabAdmin')}
                </button>
                <button
                  type="button"
                  className={`lucky-wallet__tab ${transferKind === 'user' ? 'is-on' : ''}`}
                  onClick={() => {
                    setTransferKind('user')
                    setForm(emptyForm)
                  }}
                >
                  {t('cashOut.tabUser')}
                </button>
              </div>

              {transferKind === 'admin' ? (
                <>
                  <label className="lucky-wallet__label">
                    {t('cashOut.walletAddress')}
                    <input
                      type="text"
                      value={form.walletAddress}
                      onChange={(e) => setForm({ ...form, walletAddress: e.target.value })}
                      placeholder={t('cashOut.walletPlaceholder')}
                      className="lucky-wallet__input"
                      dir="ltr"
                    />
                  </label>
                </>
              ) : (
                <label className="lucky-wallet__label">
                  {t('cashOut.recipientLabel')}
                  <input
                    type="text"
                    value={form.recipient}
                    onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                    placeholder={t('cashOut.recipientPlaceholder')}
                    className="lucky-wallet__input"
                    dir="ltr"
                  />
                </label>
              )}

              <label className="lucky-wallet__label">
                {t('cashOut.coinType')}
                <select
                  value={form.coinType}
                  onChange={(e) => setForm({ ...form, coinType: e.target.value as CoinType })}
                  className="lucky-wallet__input"
                >
                  {COIN_TYPES.map((coinType) => (
                    <option key={coinType} value={coinType}>
                      {t(coinNameKey(coinType))} — {coins[coinType]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="lucky-wallet__label">
                {t('cashOut.amountLabel')}
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder={t('cashOut.amountPlaceholder')}
                  min={1}
                  max={coins[form.coinType] || 0}
                  className="lucky-wallet__input"
                />
              </label>

              <button type="button" className="lucky-wallet__cta" onClick={handleSubmit} disabled={busy}>
                {busy
                  ? t('common.sending')
                  : transferKind === 'admin'
                    ? t('cashOut.submitAdmin')
                    : t('cashOut.submitUser')}
              </button>
            </div>
          )}
        </section>

        <section className="lucky-wallet__card">
          <h2 className="lucky-wallet__title">{t('cashOut.historyTitle')}</h2>
          {transfers.length === 0 ? (
            <p className="lucky-wallet__empty">{t('cashOut.historyEmpty')}</p>
          ) : (
            <div className="lucky-wallet__history">
              {transfers.map((tr: CoinTransfer) => (
                <div key={tr.id} className="lucky-wallet__hist-row">
                  <div className="lucky-wallet__hist-top">
                    <strong>
                      {t(coinNameKey(tr.coin_type))} · {tr.amount}
                    </strong>
                    <em className={`lucky-wallet__badge lucky-wallet__badge--${tr.status}`}>
                      {statusLabel(tr.status)}
                    </em>
                  </div>
                  <div className="lucky-wallet__hist-meta">
                    <span>
                      {tr.transfer_type === 'user'
                        ? t('cashOut.historyToUser', { id: tr.to_user_id ?? '—' })
                        : t('cashOut.historyToAdmin')}
                    </span>
                    <span>{new Date(tr.created_at).toLocaleDateString(numberLocale)}</span>
                  </div>
                  {tr.wallet_address && (
                    <p className="lucky-wallet__addr" dir="ltr">
                      {tr.wallet_address}
                    </p>
                  )}
                  {!tr.wallet_address && tr.card_number && (
                    <p className="lucky-wallet__addr">{t('common.cardMasked', { last4: tr.card_number.slice(-4) })}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {pinModal && (
        <div className="lucky-wallet__modal" role="dialog" aria-modal="true">
          <div className="lucky-wallet__modal-card">
            <h3>{pinModal === 'set' ? t('cashOut.modalSetTitle') : t('cashOut.modalUnlockTitle')}</h3>
            <p>
              {pinModal === 'set' ? t('cashOut.modalSetBody') : t('cashOut.modalUnlockBody')}
            </p>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t('cashOut.pinPlaceholder')}
              className="lucky-wallet__input lucky-wallet__input--pin"
              maxLength={6}
              inputMode="numeric"
              autoFocus
            />
            <div className="lucky-wallet__modal-actions">
              <button
                type="button"
                className="lucky-wallet__btn-sec"
                onClick={() => {
                  setPinModal(null)
                  setPinInput('')
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="lucky-wallet__cta"
                onClick={handlePINSubmit}
                disabled={pinInput.length < 4 || setPINMutation.isPending || verifyPINMutation.isPending}
              >
                {pinModal === 'set' ? t('cashOut.modalSetSubmit') : t('cashOut.modalUnlockSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
